import { ref, computed, onMounted, onUnmounted } from 'vue'
import { getMedia } from '@touchvue/utils'

/**
 * 设备类型
 */
export type DeviceType = 'pc' | 'pad' | 'phone'

/**
 * 全局设备检测管理器
 * 使用单例模式避免重复注册resize事件
 */
class DeviceManager {
	private static instance: DeviceManager
	private deviceType = ref<DeviceType>(getMedia())
	private listeners = new Set<() => void>()
	private resizeHandler: (() => void) | null = null

	private constructor() {
		this.initResizeListener()
	}

	static getInstance(): DeviceManager {
		if (!DeviceManager.instance) {
			DeviceManager.instance = new DeviceManager()
		}
		return DeviceManager.instance
	}

	private initResizeListener() {
		if (typeof window !== 'undefined') {
			this.resizeHandler = () => {
				const newDeviceType = getMedia()
				if (this.deviceType.value !== newDeviceType) {
					this.deviceType.value = newDeviceType
					// 通知所有监听器
					this.listeners.forEach(listener => listener())
				}
			}
			window.addEventListener('resize', this.resizeHandler)
		}
	}

	getDeviceType() {
		return this.deviceType
	}

	addListener(listener: () => void) {
		this.listeners.add(listener)
	}

	removeListener(listener: () => void) {
		this.listeners.delete(listener)

		// 如果没有监听器了，移除resize事件
		if (this.listeners.size === 0 && this.resizeHandler && typeof window !== 'undefined') {
			window.removeEventListener('resize', this.resizeHandler)
			this.resizeHandler = null
		}
	}

	destroy() {
		if (this.resizeHandler && typeof window !== 'undefined') {
			window.removeEventListener('resize', this.resizeHandler)
		}
		this.listeners.clear()
		this.resizeHandler = null
	}
}

/**
 * 设备检测 Hook
 * 提供响应式的设备类型判断，替代 Vue2 中的 $phone、$pc、$pad
 * 使用单例模式避免重复注册resize事件监听器
 */
export function useDevice() {
	const manager = DeviceManager.getInstance()
	const deviceType = manager.getDeviceType()

	// 计算属性：是否为手机
	const isPhone = computed(() => deviceType.value === 'phone')

	// 计算属性：是否为平板
	const isPad = computed(() => deviceType.value === 'pad')

	// 计算属性：是否为PC
	const isPc = computed(() => deviceType.value === 'pc')

	// 计算属性：是否为移动设备（手机或平板）
	const isMobile = computed(() => deviceType.value === 'phone' || deviceType.value === 'pad')

	// 更新设备类型的回调
	const updateCallback = () => {
		// 这里不需要做任何事情，因为deviceType是响应式的
		// 当manager更新deviceType时，所有计算属性会自动更新
	}

	// 手动更新设备类型
	const updateDeviceType = () => {
		const newDeviceType = getMedia()
		if (deviceType.value !== newDeviceType) {
			// 直接更新manager中的deviceType
			;(deviceType as any).value = newDeviceType
		}
	}

	// 组件挂载时添加监听器
	onMounted(() => {
		manager.addListener(updateCallback)
	})

	// 组件卸载时移除监听器
	onUnmounted(() => {
		manager.removeListener(updateCallback)
	})

	return {
		// 设备类型（响应式）
		deviceType,

		// 响应式判断
		isPhone,
		isPad,
		isPc,
		isMobile,

		// 手动更新设备类型
		updateDeviceType
	}
}

/**
 * 获取当前设备类型（非响应式）
 * @returns 设备类型字符串
 */
export function getDeviceType(): DeviceType {
	return getMedia()
}

/**
 * 判断是否为手机设备（非响应式）
 * @returns 是否为手机
 */
export function isPhoneDevice(): boolean {
	return getMedia() === 'phone'
}

/**
 * 判断是否为平板设备（非响应式）
 * @returns 是否为平板
 */
export function isPadDevice(): boolean {
	return getMedia() === 'pad'
}

/**
 * 判断是否为PC设备（非响应式）
 * @returns 是否为PC
 */
export function isPcDevice(): boolean {
	return getMedia() === 'pc'
}

/**
 * 判断是否为移动设备（非响应式）
 * @returns 是否为移动设备（手机或平板）
 */
export function isMobileDevice(): boolean {
	const device = getMedia()
	return device === 'phone' || device === 'pad'
}
